<template>
  <header ref="navigationRoot" class="nav-header" :dir="isArabic ? 'rtl' : 'ltr'">
    <nav class="nav-shell" :aria-label="t.mainNavigation">
      <a class="brand" :href="mainSiteUrl" @click="closeAll">
        <img src="@/assets/Splendapp-web.svg" :alt="t.logoAlt">
      </a>

      <button class="menu-toggle" type="button" :aria-expanded="mobileOpen.toString()" aria-controls="primary-navigation" :aria-label="mobileOpen ? t.closeMenu : t.openMenu" @click="mobileOpen = !mobileOpen">
        <svg v-if="!mobileOpen" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
        <svg v-else viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>
      </button>

      <div id="primary-navigation" class="navigation" :class="{ 'navigation--open': mobileOpen }">
        <ul class="nav-list">
          <li v-for="group in groups" :key="group.id" class="nav-group">
            <button type="button" class="nav-trigger" :aria-expanded="(openDropdown === group.id).toString()" :aria-controls="`nav-panel-${group.id}`" @click="toggleDropdown(group.id)">
              {{ group.label }}
              <svg viewBox="0 0 12 8" aria-hidden="true"><path d="m1 1 5 5 5-5"/></svg>
            </button>
            <div v-show="openDropdown === group.id" :id="`nav-panel-${group.id}`" class="dropdown-panel" :class="{ 'dropdown-panel--wide': group.id === 'image' }">
              <p>{{ group.description }}</p>
              <div class="dropdown-links">
                <router-link v-for="item in group.items" :key="item.id" :to="item.to" @click.native="closeAll">
                  <span>{{ item.label }}</span>
                  <small v-if="item.description">{{ item.description }}</small>
                </router-link>
              </div>
              <router-link class="view-all" :to="group.allTo" @click.native="closeAll">{{ t.viewGroup }} <span aria-hidden="true">{{ isArabic ? '←' : '→' }}</span></router-link>
            </div>
          </li>
        </ul>

        <div class="nav-actions">
          <router-link v-if="showHomeButton" class="home-link" :to="{ name: 'home' }" @click.native="closeAll">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 11.5 12 4l9 7.5M5.5 10v10h13V10M9.5 20v-6h5v6"/></svg>
            <span>{{ t.backHome }}</span>
          </router-link>
          <button class="language-button" type="button" :aria-label="t.switchLanguage" @click="changeLanguage">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M3.5 12h17M12 3c2.3 2.5 3.5 5.5 3.5 9S14.3 18.5 12 21M12 3C9.7 5.5 8.5 8.5 8.5 12s1.2 6.5 3.5 9"/></svg>
            <span>{{ isArabic ? 'English' : 'العربية' }}</span>
          </button>
          <router-link v-if="!showHomeButton" class="all-tools-link" :to="{ name: 'home', hash: '#tools' }" @click.native="closeAll">{{ t.allTools }}</router-link>
          <a class="website-cta" :href="mainSiteUrl" @click="trackWebsiteClick">{{ t.websiteCta }}</a>
        </div>
      </div>
    </nav>
    <button v-if="mobileOpen" class="nav-backdrop" type="button" :aria-label="t.closeMenu" @click="closeAll"></button>
  </header>
</template>

<script>
import { tools, localizedTool } from '@/tools/catalog'
import { routeSeo } from '@/seo/routeSeo'
import { trackPromotionClick } from '@/utils/promotionAnalytics'

const COPY = {
  en: {
    mainNavigation: 'Main navigation', openMenu: 'Open navigation menu', closeMenu: 'Close navigation menu', switchLanguage: 'Switch to Arabic', allTools: 'All Tools', backHome: 'Back to Home', viewGroup: 'View all', logoAlt: 'SplendApp', websiteCta: 'Build Your Mobile App',
    image: 'Image Tools', imageDescription: 'Edit, optimize and create images in your browser.',
    codes: 'QR & Barcode', codesDescription: 'Generate and scan QR codes and popular barcode formats.',
    seo: 'SEO Tools', seoDescription: 'Analyze metadata and create technical SEO markup.',
    business: 'Business', businessDescription: 'Estimate booking losses and mobile app impact.'
  },
  ar: {
    mainNavigation: 'التنقل الرئيسي', openMenu: 'فتح قائمة التنقل', closeMenu: 'إغلاق قائمة التنقل', switchLanguage: 'التبديل إلى الإنجليزية', allTools: 'كل الأدوات', backHome: 'العودة للرئيسية', viewGroup: 'عرض الكل', logoAlt: 'سبلينداب', websiteCta: 'أنشئ تطبيقك',
    image: 'أدوات الصور', imageDescription: 'عدّل الصور وحسّنها وأنشئ محتوى جديدًا داخل متصفحك.',
    codes: 'QR والباركود', codesDescription: 'أنشئ رموز QR والباركود واقرأها من الصور أو الكاميرا.',
    seo: 'أدوات تحسين محركات البحث', seoDescription: 'حلّل بيانات الصفحات وأنشئ أكواد SEO التقنية.',
    business: 'الأعمال', businessDescription: 'قدّر خسائر الحجوزات وأثر تطبيق الجوال.'
  }
}

const IMAGE_CATEGORIES = ['image-editing', 'image-optimization', 'image-creation', 'privacy-effects']

export default {
  name: 'NavBar',
  data: () => ({ mobileOpen: false, openDropdown: '' }),
  computed: {
    isArabic() { return this.$store.state.currentLanguage === 'ar' },
    language() { return this.isArabic ? 'ar' : 'en' },
    t() { return COPY[this.language] },
    mainSiteUrl() { return this.isArabic ? 'https://splendapp.com' : 'https://splendapp.com/en/' },
    showHomeButton() { return !['home', 'not-found'].includes(this.$route.name) },
    groups() {
      return [
        {
          id: 'image', label: this.t.image, description: this.t.imageDescription,
          items: [
            this.categoryItem('image-editing', 'Image Editing', 'تعديل الصور'),
            this.categoryItem('image-optimization', 'Image Optimization', 'تحسين الصور'),
            this.categoryItem('image-creation', 'Image Creation', 'إنشاء الصور'),
            this.categoryItem('privacy-effects', 'Image Privacy / Effects', 'خصوصية وتأثيرات الصور')
          ],
          allTo: this.categoryTo('image-editing')
        },
        this.toolGroup('codes', this.t.codes, this.t.codesDescription, 'qr-barcode'),
        this.toolGroup('seo', this.t.seo, this.t.seoDescription, 'seo'),
        this.toolGroup('business', this.t.business, this.t.businessDescription, 'business')
      ]
    }
  },
  watch: {
    '$route.fullPath'() { this.closeAll() }
  },
  mounted() {
    document.addEventListener('keydown', this.handleEscape)
    document.addEventListener('pointerdown', this.handleOutsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscape)
    document.removeEventListener('pointerdown', this.handleOutsideClick)
  },
  methods: {
    categoryTo(category) { return { name: 'home', query: { category }, hash: '#tools' } },
    categoryItem(id, en, ar) { return { id, label: this.isArabic ? ar : en, to: this.categoryTo(id) } },
    toolGroup(id, label, description, category) {
      const items = tools.filter(tool => tool.category === category).map(tool => {
        const localized = localizedTool(tool, this.language)
        return {
          id: tool.id,
          label: localized.title,
          description: localized.description,
          to: localized.href || localized.path || { name: localized.routeName }
        }
      })
      return { id, label, description, items, allTo: this.categoryTo(category) }
    },
    toggleDropdown(id) { this.openDropdown = this.openDropdown === id ? '' : id },
    closeAll() { this.mobileOpen = false; this.openDropdown = '' },
    handleEscape(event) { if (event.key === 'Escape') this.closeAll() },
    handleOutsideClick(event) {
      const navigationRoot = this.$refs.navigationRoot
      if ((this.mobileOpen || this.openDropdown) && navigationRoot && !navigationRoot.contains(event.target)) this.closeAll()
    },
    trackWebsiteClick() {
      trackPromotionClick(this.$gtag, 'header_website_cta', {
        language: this.language,
        source_route: this.$route.name || 'unknown'
      })
      this.closeAll()
    },
    changeLanguage() {
      const nextLanguage = this.isArabic ? 'en' : 'ar'
      const currentSeo = routeSeo[this.$route.name]
      const alternate = currentSeo?.alternate && routeSeo[currentSeo.alternate]
      this.$store.commit('setCurrentLanguage', nextLanguage)
      this.closeAll()
      if (alternate && alternate.lang === nextLanguage) this.$router.push({ name: alternate.name })
    }
  }
}
</script>

<style scoped>
.nav-header { position: sticky; top: 0; z-index: 1000; border-bottom: 1px solid rgba(185,200,221,.72); background: rgba(255,255,255,.94); box-shadow: 0 6px 24px rgba(36,54,92,.06); backdrop-filter: blur(16px); font-family: "Cairo", sans-serif; }
.nav-shell { display: flex; align-items: center; gap: 34px; width: min(1240px, calc(100% - 40px)); min-height: 72px; margin-inline: auto; }
.brand { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 10px; color: #172b4d; text-decoration: none; }
.brand img { display: block; width: 150px; height: auto; }
.navigation { display: flex; flex: 1; align-items: center; justify-content: space-between; gap: 24px; }
.nav-list, .nav-actions { display: flex; align-items: center; margin: 0; padding: 0; list-style: none; }
.nav-list { gap: 2px; }.nav-actions { gap: 10px; }
.nav-group { position: relative; }
.nav-trigger, .language-button, .menu-toggle { border: 0; background: transparent; color: #29405f; font: inherit; cursor: pointer; }
.nav-trigger { display: inline-flex; align-items: center; gap: 7px; min-height: 44px; padding: 9px 12px; border-radius: 10px; font-size: .9rem; font-weight: 800; }
.nav-trigger:hover, .nav-trigger[aria-expanded='true'] { background: #eef4ff; color: #194fae; }
.nav-trigger svg { width: 10px; fill: none; stroke: currentColor; stroke-width: 1.8; transition: transform .2s; }
.nav-trigger[aria-expanded='true'] svg { transform: rotate(180deg); }
.dropdown-panel { position: absolute; inset-block-start: calc(100% + 10px); inset-inline-start: 0; width: 330px; padding: 20px; border: 1px solid #d8e2ef; border-radius: 18px; background: #fff; box-shadow: 0 22px 55px rgba(23,43,77,.18); }
[dir='rtl'] .dropdown-panel { inset-inline-start: auto; inset-inline-end: 0; }
.dropdown-panel--wide { width: 390px; }
.dropdown-panel > p { margin: 0 0 14px; color: #64748b; font-size: .79rem; line-height: 1.65; }
.dropdown-links { display: grid; grid-template-columns: 1fr; gap: 5px; }
.dropdown-panel--wide .dropdown-links { grid-template-columns: 1fr 1fr; }
.dropdown-links a { display: flex; flex-direction: column; gap: 3px; padding: 10px; border-radius: 10px; color: #172b4d; text-decoration: none; }
.dropdown-links a:hover { background: #f1f6ff; color: #194fae; }
.dropdown-links span { font-size: .88rem; font-weight: 900; }.dropdown-links small { display: -webkit-box; overflow: hidden; color: #718096; font-size: .7rem; line-height: 1.45; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.view-all { display: flex; justify-content: space-between; margin-top: 12px; padding: 11px 10px 2px; border-top: 1px solid #e3eaf4; color: #245dcc; font-size: .82rem; font-weight: 900; text-decoration: none; }
.language-button { display: inline-flex; align-items: center; gap: 7px; min-height: 42px; padding: 8px 10px; border-radius: 10px; font-size: .84rem; font-weight: 800; }
.language-button:hover { background: #f1f5f9; }.language-button svg { width: 19px; fill: none; stroke: currentColor; stroke-width: 1.5; }
.home-link { display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 42px; padding: 8px 13px; border: 1px solid #b8cdf5; border-radius: 10px; background: #eef4ff; color: #194fae; font-size: .84rem; font-weight: 900; text-decoration: none; white-space: nowrap; }
.home-link:hover { border-color: #86a9eb; background: #dfeaff; color: #123f91; }
.home-link svg { width: 18px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.all-tools-link { padding: 8px 6px; color: #245dcc; font-size: .84rem; font-weight: 900; text-decoration: none; white-space: nowrap; }
.all-tools-link:hover { color: #194fae; }
.website-cta { display: inline-flex; align-items: center; justify-content: center; min-height: 42px; padding: 9px 17px; border-radius: 999px; background: #245dcc; color: #fff; font-size: .84rem; font-weight: 900; text-decoration: none; white-space: nowrap; box-shadow: 0 8px 18px rgba(36,93,204,.2); }
.website-cta:hover { background: #194fae; }
.menu-toggle { display: none; margin-inline-start: auto; width: 44px; height: 44px; padding: 10px; border-radius: 10px; }
.menu-toggle:hover { background: #eef4ff; }.menu-toggle svg { width: 24px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; }
.nav-trigger:focus-visible, .language-button:focus-visible, .home-link:focus-visible, .all-tools-link:focus-visible, .website-cta:focus-visible, .brand:focus-visible, .menu-toggle:focus-visible, .dropdown-links a:focus-visible, .view-all:focus-visible { outline: 3px solid #f59e0b; outline-offset: 3px; }
.nav-backdrop { display: none; }
@media (max-width: 1050px) {
  .nav-shell { min-height: 66px; }.menu-toggle { display: grid; place-items: center; }
  .navigation { display: none; position: absolute; z-index: 2; inset-block-start: 66px; inset-inline: 0; max-height: calc(100vh - 66px); padding: 16px 20px 24px; overflow-y: auto; border-top: 1px solid #d8e2ef; background: #fff; }
  .navigation--open { display: block; }
  .nav-list, .nav-actions { align-items: stretch; flex-direction: column; gap: 7px; }
  .nav-group { width: 100%; }.nav-trigger { justify-content: space-between; width: 100%; min-height: 48px; padding-inline: 12px; }
  .dropdown-panel, .dropdown-panel--wide { position: static; width: auto; margin: 2px 0 8px; padding: 12px; border: 0; border-radius: 12px; background: #f7f9fc; box-shadow: none; }
  .dropdown-panel--wide .dropdown-links { grid-template-columns: 1fr 1fr; }
  .nav-actions { margin-top: 14px; padding-top: 16px; border-top: 1px solid #e3eaf4; }
  .language-button, .home-link, .all-tools-link, .website-cta { width: 100%; }.language-button, .home-link, .all-tools-link { justify-content: center; text-align: center; }
  .nav-backdrop { display: block; position: fixed; z-index: -1; inset: 66px 0 0; width: 100%; border: 0; background: rgba(15,23,42,.38); }
}
@media (max-width: 540px) {
  .nav-shell { width: min(100% - 24px, 1240px); }.brand img { width: 136px; }
  .dropdown-panel--wide .dropdown-links { grid-template-columns: 1fr; }
}
</style>
